import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import styled from "styled-components";

import {
  AdminButton,
} from "../action_engine";

import {
  AdminSection,
  AdminSectionBadge,
  AdminSectionHeader,
} from "../section_engine";

import {
  AdminErrorState,
  AdminLoadingState,
  AdminSuccessState,
} from "../ui_engine";

import {
  activateEmployee,
  archiveEmployee,
  attachEmployeeUser,
  beginEmployeeOffboarding,
  deactivateEmployee,
  detachEmployeeUser,
  startEmployeePreboarding,
  updateEmployeeIdentity,
} from "../../../services/hrAdminApi";

import {
  EMPLOYEE_ADMIN_ACTION_STATUS,
  createEmployeeAdminActionRunner,
} from "./employeeAdminActions";


export const EMPTY_EMPLOYEE_ACTION_AVAILABILITY =
  Object.freeze({
    identity: false,
    lifecycle: false,
    relationship: false,
  });


const LIFECYCLE_PRESENTATION = Object.freeze({
  preboarding: [
    "activate",
  ],

  active: [
    "begin-offboarding",
    "deactivate",
  ],

  offboarding: [
    "deactivate",
  ],

  inactive: [
    "activate",
    "archive",
  ],

  archived: [],
});


const LIFECYCLE_ACTIONS = Object.freeze({
  "start-preboarding": {
    label: "Démarrer le préboarding",
    mutation: startEmployeePreboarding,
  },

  activate: {
    label: "Activer",
    mutation: activateEmployee,
  },

  "begin-offboarding": {
    label: "Commencer l’offboarding",
    mutation: beginEmployeeOffboarding,
    requestReason: true,
  },

  deactivate: {
    label: "Désactiver",
    mutation: deactivateEmployee,
    requestReason: true,
  },

  archive: {
    label: "Archiver",
    mutation: archiveEmployee,
    requestReason: true,
    destructive: true,
  },
});


function normalizeAvailability(value) {
  return {
    ...EMPTY_EMPLOYEE_ACTION_AVAILABILITY,
    ...(value || {}),
  };
}


function errorMessage(status) {
  switch (status) {
    case EMPLOYEE_ADMIN_ACTION_STATUS.VALIDATION_ERROR:
      return "Les données saisies ne sont pas valides.";

    case EMPLOYEE_ADMIN_ACTION_STATUS.FORBIDDEN:
      return "Cette action n’est pas autorisée.";

    case EMPLOYEE_ADMIN_ACTION_STATUS.NOT_FOUND:
      return "Le salarié demandé est introuvable.";

    case EMPLOYEE_ADMIN_ACTION_STATUS.CONFLICT:
      return "Cette action entre en conflit avec l’état actuel du salarié.";

    default:
      return "Impossible d’exécuter cette action.";
  }
}


export default function EmployeeAdminActionsPanel({
  employee,
  user,
  actionAvailability =
    EMPTY_EMPLOYEE_ACTION_AVAILABILITY,
  confirmAction,
  promptAction,
  onEmployeeChanged,
}) {
  const availability =
    normalizeAvailability(
      actionAvailability,
    );

  const [
    actionState,
    setActionState,
  ] = useState({
    status:
      EMPLOYEE_ADMIN_ACTION_STATUS.IDLE,
    error: null,
    data: null,
  });

  const [
    editingIdentity,
    setEditingIdentity,
  ] = useState(false);

  const [
    identityDraft,
    setIdentityDraft,
  ] = useState({
    employeeNumber:
      employee?.employee_number ?? "",
    firstName:
      employee?.first_name ?? "",
    lastName:
      employee?.last_name ?? "",
  });

  const runnerRef = useRef(null);
  const errorFocusRef = useRef(null);

  if (!runnerRef.current) {
    runnerRef.current =
      createEmployeeAdminActionRunner({
        onStateChange:
          setActionState,
      });
  }

  const hasActions =
    availability.identity
    || availability.lifecycle
    || availability.relationship;

  const pending =
    actionState.status
    === EMPLOYEE_ADMIN_ACTION_STATUS.PENDING;

  const errorStatuses = [
    EMPLOYEE_ADMIN_ACTION_STATUS.VALIDATION_ERROR,
    EMPLOYEE_ADMIN_ACTION_STATUS.FORBIDDEN,
    EMPLOYEE_ADMIN_ACTION_STATUS.NOT_FOUND,
    EMPLOYEE_ADMIN_ACTION_STATUS.CONFLICT,
    EMPLOYEE_ADMIN_ACTION_STATUS.GENERIC_ERROR,
  ];

  const hasActionError =
    errorStatuses.includes(
      actionState.status,
    );

  useEffect(() => {
    if (!hasActionError) {
      return;
    }

    errorFocusRef.current?.focus({
      preventScroll: true,
    });
  }, [
    hasActionError,
    actionState.status,
  ]);

  const lifecycleActions =
    useMemo(
      () => (
        LIFECYCLE_PRESENTATION[
          employee?.status
        ] || []
      ),
      [employee?.status],
    );

  if (!hasActions) {
    return null;
  }

  const runMutation = async (
    mutation,
  ) => {
    try {
      const result =
        await runnerRef.current.run(
          mutation,
        );

      if (
        result?.accepted
        && typeof onEmployeeChanged
          === "function"
      ) {
        await onEmployeeChanged();
      }

      return result;
    } catch {
      return null;
    }
  };

  const requestConfirmation =
    async ({
      title,
      message,
      confirmLabel,
    }) => {
      if (
        typeof confirmAction
        !== "function"
      ) {
        return false;
      }

      return Boolean(
        await confirmAction({
          title,
          message,
          confirmLabel,
        }),
      );
    };

  const requestPrompt =
    async ({
      title,
      message,
      confirmLabel,
      placeholder,
    }) => {
      if (
        typeof promptAction
        !== "function"
      ) {
        return null;
      }

      return promptAction({
        title,
        message,
        confirmLabel,
        placeholder,
      });
    };

  const executeLifecycle =
    async (actionName) => {
      const contract =
        LIFECYCLE_ACTIONS[actionName];

      if (!contract) {
        return;
      }

      let reason;

      if (contract.requestReason) {
        reason =
          await requestPrompt({
            title: contract.label,
            message:
              "Vous pouvez préciser un motif administratif.",
            confirmLabel: "Continuer",
            placeholder: "Motif",
          });

        if (reason === null) {
          return;
        }
      }

      const confirmed =
        await requestConfirmation({
          title: contract.label,
          message:
            "Confirmer cette action sur le salarié ?",
          confirmLabel:
            contract.destructive
              ? "Archiver"
              : "Confirmer",
        });

      if (!confirmed) {
        return;
      }

      await runMutation(
        () => contract.mutation({
          employeeUuid:
            employee?.uuid,
          reason:
            reason || undefined,
        }),
      );
    };

  const saveIdentity = async () => {
    const result =
      await runMutation(
        () => updateEmployeeIdentity({
          employeeUuid:
            employee?.uuid,
          ...identityDraft,
        }),
      );

    if (
      result?.accepted
    ) {
      setEditingIdentity(false);
    }
  };

  const attachUser = async () => {
    const userUuid =
      await requestPrompt({
        title: "Lier un utilisateur",
        message:
          "Saisissez l’identifiant UUID du User à associer.",
        confirmLabel: "Lier",
        placeholder: "user_uuid",
      });

    if (!userUuid) {
      return;
    }

    await runMutation(
      () => attachEmployeeUser({
        employeeUuid:
          employee?.uuid,
        userUuid,
      }),
    );
  };

  const detachUser = async () => {
    const confirmed =
      await requestConfirmation({
        title: "Dissocier l’utilisateur",
        message:
          "Confirmer la suppression de la relation Employee ↔ User ?",
        confirmLabel: "Dissocier",
      });

    if (!confirmed) {
      return;
    }

    await runMutation(
      () => detachEmployeeUser({
        employeeUuid:
          employee?.uuid,
      }),
    );
  };

  return (
    <AdminSection
      aria-labelledby={
        "employee-admin-actions-title"
      }
    >
      <AdminSectionHeader>
        <AdminSectionBadge>
          Actions
        </AdminSectionBadge>

        <h2
          id="employee-admin-actions-title"
        >
          Actions administratives
        </h2>

        <p>
          Les actions sont déléguées aux
          autorités métier Employee existantes.
        </p>
      </AdminSectionHeader>

      {pending ? (
        <AdminLoadingState
          compact
          message="Action en cours..."
        />
      ) : null}

      {actionState.status
        === EMPLOYEE_ADMIN_ACTION_STATUS.SUCCESS ? (
        <AdminSuccessState
          compact
          message="Action enregistrée."
        />
      ) : null}

      {hasActionError ? (
        <ActionErrorFocusTarget
          ref={errorFocusRef}
          tabIndex={-1}
        >
          <AdminErrorState
            compact
            message={
              errorMessage(
                actionState.status,
              )
            }
          />
        </ActionErrorFocusTarget>
      ) : null}

      {availability.identity ? (
        <ActionGroup>
          <h3>Identité Employee</h3>

          {!editingIdentity ? (
            <AdminButton
              type="button"
              variant="secondary"
              disabled={pending}
              onClick={() => {
                setIdentityDraft({
                  employeeNumber:
                    employee
                      ?.employee_number
                    ?? "",
                  firstName:
                    employee
                      ?.first_name
                    ?? "",
                  lastName:
                    employee
                      ?.last_name
                    ?? "",
                });

                setEditingIdentity(true);
              }}
            >
              Modifier l’identité
            </AdminButton>
          ) : (
            <IdentityForm
              onSubmit={(event) => {
                event.preventDefault();
                saveIdentity();
              }}
            >
              <label>
                Matricule
                <input
                  value={
                    identityDraft
                      .employeeNumber
                  }
                  disabled={pending}
                  onChange={(event) => {
                    setIdentityDraft(
                      (current) => ({
                        ...current,
                        employeeNumber:
                          event.target.value,
                      }),
                    );
                  }}
                />
              </label>

              <label>
                Prénom
                <input
                  value={
                    identityDraft
                      .firstName
                  }
                  disabled={pending}
                  onChange={(event) => {
                    setIdentityDraft(
                      (current) => ({
                        ...current,
                        firstName:
                          event.target.value,
                      }),
                    );
                  }}
                />
              </label>

              <label>
                Nom
                <input
                  value={
                    identityDraft
                      .lastName
                  }
                  disabled={pending}
                  onChange={(event) => {
                    setIdentityDraft(
                      (current) => ({
                        ...current,
                        lastName:
                          event.target.value,
                      }),
                    );
                  }}
                />
              </label>

              <ActionRow>
                <AdminButton
                  type="submit"
                  variant="primary"
                  loading={pending}
                  disabled={pending}
                >
                  Enregistrer
                </AdminButton>

                <AdminButton
                  type="button"
                  variant="secondary"
                  disabled={pending}
                  onClick={() => {
                    setEditingIdentity(
                      false,
                    );
                  }}
                >
                  Annuler
                </AdminButton>
              </ActionRow>
            </IdentityForm>
          )}
        </ActionGroup>
      ) : null}

      {availability.lifecycle ? (
        <ActionGroup>
          <h3>Lifecycle Employee</h3>

          <ActionRow>
            {lifecycleActions.map(
              (actionName) => {
                const contract =
                  LIFECYCLE_ACTIONS[
                    actionName
                  ];

                return (
                  <AdminButton
                    key={actionName}
                    type="button"
                    variant={
                      contract
                        ?.destructive
                        ? "danger"
                        : "secondary"
                    }
                    disabled={pending}
                    onClick={() => {
                      executeLifecycle(
                        actionName,
                      );
                    }}
                  >
                    {contract.label}
                  </AdminButton>
                );
              },
            )}

            {lifecycleActions.length
              === 0 ? (
              <ReadOnlyNote>
                Aucune action lifecycle
                proposée pour cet état.
              </ReadOnlyNote>
            ) : null}
          </ActionRow>
        </ActionGroup>
      ) : null}

      {availability.relationship ? (
        <ActionGroup>
          <h3>Relation User</h3>

          {user ? (
            <AdminButton
              type="button"
              variant="secondary"
              disabled={pending}
              onClick={detachUser}
            >
              Dissocier le User
            </AdminButton>
          ) : (
            <AdminButton
              type="button"
              variant="secondary"
              disabled={pending}
              onClick={attachUser}
            >
              Lier un User
            </AdminButton>
          )}
        </ActionGroup>
      ) : null}
    </AdminSection>
  );
}


const ActionErrorFocusTarget = styled.div`
  min-width: 0;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline:
      2px solid
      var(--q360-button-focus-ring, #ffffff);

    outline-offset: 3px;

    border-radius: 12px;
  }
`;


const ActionGroup = styled.div`
  display: grid;
  gap: 0.75rem;

  & + & {
    margin-top: 1rem;
  }

  h3 {
    margin: 0;
    font-size: 0.95rem;
  }
`;


const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  align-items: center;
`;


const IdentityForm = styled.form`
  display: grid;
  gap: 0.75rem;

  grid-template-columns:
    repeat(
      auto-fit,
      minmax(180px, 1fr)
    );

  label {
    display: grid;
    gap: 0.35rem;

    min-width: 0;

    font-size: 0.82rem;
    font-weight: 700;
  }

  input {
    min-width: 0;
    width: 100%;

    padding: 0.65rem 0.7rem;

    border:
      1px solid
      rgba(255, 255, 255, 0.16);

    border-radius: 10px;

    background:
      rgba(11, 19, 40, 0.36);

    color: inherit;
  }

  ${ActionRow} {
    grid-column: 1 / -1;
  }
`;


const ReadOnlyNote = styled.p`
  margin: 0;

  color:
    rgba(247, 247, 247, 0.7);

  overflow-wrap: anywhere;
`;
